import React, { useState } from 'react';
import {
  Box, Grid, Typography, Paper, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  TextField, Button, Snackbar, Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Link, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import { PageHeader, MetricCard, StatusChip, SkeletonCards, ErrorState, PermissionState, ConfirmDialog, useDesignTokens } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import useAdminDashboardPage from './useAdminDashboardPage';
import useCompanyName from '../../hooks/useCompanyName';

const ATTENTION_ICONS = {
  pending_orders: ScheduleIcon,
  low_stock: Inventory2Icon,
  invoice_issue: ReceiptLongIcon,
};

// `value` is already a whole-number percent from the server (18 means
// "+18%") — this only picks a colour from the sign and appends the symbol,
// it never multiplies or divides the figure itself.
const pctDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}%` };
};

const countDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}` };
};

// Outstanding balance falling is good news, so a negative delta maps to the
// "up"/green treatment and a positive one to "down"/red — inverted from a
// literal sign reading, same as MetricCard already supports via `direction`.
const invertedCurrencyDelta = (value) => {
  if (value == null) return undefined;
  return {
    direction: value < 0 ? 'up' : value > 0 ? 'down' : 'neutral',
    text: `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`,
  };
};

const AdminDashboard = () => {
  const p = useAdminDashboardPage();
  const companyName = useCompanyName();
  const { tokens: t } = useDesignTokens();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOrder, setMenuOrder] = useState(null);
  const closeOrderMenu = () => setMenuAnchor(null);

  if (p.effectiveStatus === 'loading') {
    return (
      <Box>
        <SkeletonCards rows={1} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => <Grid item xs={6} sm={3} key={i}><SkeletonCards rows={1} /></Grid>)}
        </Grid>
        <SkeletonCards rows={3} />
      </Box>
    );
  }

  if (p.effectiveStatus === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load dashboard data", { defaultValue: "Couldn't load dashboard data" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={() => p.fetchMetrics(false)}
      />
    );
  }

  if (p.effectiveStatus === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to the Dashboard", { defaultValue: "You don't have access to the Dashboard" })}
        description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
      />
    );
  }

  const d = p.data;

  return (
    <Box>
      <PageHeader
        title={p.t('Good morning, {{name}}', { name: companyName || p.t('there', { defaultValue: 'there' }), defaultValue: `Good morning, ${companyName || 'there'}` })}
        subtitle={p.t("Here's what's moving today", { defaultValue: "Here's what's moving today" })}
        helpKey="dashboard"
        actions={[
          { label: p.refreshing ? p.t('Refreshing...', { defaultValue: 'Refreshing...' }) : p.t('Refresh'), icon: RefreshIcon, variant: 'outlined', onClick: p.handleRefresh, disabled: p.refreshing },
        ]}
      />

      <Grid container spacing={1.75} sx={{ mb: 2 }} data-tour="dashboard-metrics">
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Revenue today', { defaultValue: 'Revenue today' })} value={formatCurrency(d.revenueToday.value)} delta={pctDelta(d.revenueToday.delta)} sparkline={d.revenueToday.sparkline} onClick={() => navigate('/reporting')} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Orders today', { defaultValue: 'Orders today' })} value={d.ordersToday.value} delta={countDelta(d.ordersToday.delta)} sparkline={d.ordersToday.sparkline} onClick={() => navigate('/client-orders')} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            label={p.t('Pending review', { defaultValue: 'Pending review' })}
            value={d.pendingOrders.value}
            delta={d.pendingOrders.value > 0 ? { direction: 'neutral', text: p.t('needs action', { defaultValue: 'needs action' }) } : undefined}
            tone={d.pendingOrders.value > 0 ? 'warning' : undefined}
            onClick={() => navigate('/client-orders')}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard label={p.t('Outstanding', { defaultValue: 'Outstanding' })} value={formatCurrency(d.outstandingTotal.value)} delta={invertedCurrencyDelta(d.outstandingTotal.delta)} onClick={() => navigate('/invoices')} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper data-tour="dashboard-revenue" variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">{p.t('Revenue · last 14 days', { defaultValue: 'Revenue · last 14 days' })}</Typography>
              <Typography variant="body2" color="text.secondary">
                <Typography component="span" variant="body2" fontWeight={700} color="text.primary">{formatCurrency(d.revenueLast14Days.value)}</Typography>
                {d.revenueLast14Days.delta != null && (
                  <Typography component="span" variant="body2" sx={{ ml: 0.75, color: d.revenueLast14Days.delta >= 0 ? 'success.main' : 'error.main' }}>
                    {d.revenueLast14Days.delta >= 0 ? '▲' : '▼'} {Math.abs(d.revenueLast14Days.delta)}% {p.t('vs prior 14', { defaultValue: 'vs prior 14' })}
                  </Typography>
                )}
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={d.revenueDaily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={t.accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={t.accent} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={t.border} />
                <XAxis dataKey="date" tick={{ fontSize: 10.5, fill: t.muted }} tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} axisLine={{ stroke: t.border }} tickLine={false} minTickGap={30} />
                <YAxis hide />
                <RechartsTooltip
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: radius.md, fontSize: 12.5 }}
                />
                <Area type="monotone" dataKey="value" stroke={t.accent} strokeWidth={2} fill="url(#revenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper data-tour="dashboard-attention" variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Needs attention', { defaultValue: 'Needs attention' })}</Typography>
            {d.attention.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('Nothing needs your attention right now.', { defaultValue: 'Nothing needs your attention right now.' })}</Typography>
            ) : (
              d.attention.map((item) => {
                const Icon = ATTENTION_ICONS[item.kind] || ScheduleIcon;
                const linkTo = item.kind === 'pending_orders' ? '/client-orders' : item.kind === 'low_stock' ? '/inventory' : '/invoices';
                return (
                  <Box key={item.kind} component={Link} to={linkTo} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: 'action.hover' }, borderRadius: `${radius.md}px`, px: 1, mx: -1 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: `${radius.md}px`, bgcolor: 'warning.light', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon fontSize="small" sx={{ color: 'warning.dark' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {item.kind === 'pending_orders' && p.t('{{count}} orders pending review', { count: item.count, defaultValue: `${item.count} orders pending review` })}
                        {item.kind === 'low_stock' && p.t('{{count}} products low on stock', { count: item.count, defaultValue: `${item.count} products low on stock` })}
                        {item.kind === 'invoice_issue' && p.t('{{count}} invoice processing issue', { count: item.count, defaultValue: `${item.count} invoice processing issue` })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{item.detail}</Typography>
                    </Box>
                    <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
                  </Box>
                );
              })
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">{p.t('Recent orders', { defaultValue: 'Recent orders' })}</Typography>
              <Typography component={Link} to="/client-orders" variant="body2" fontWeight={600} sx={{ color: 'primary.main', textDecoration: 'none' }}>{p.t('View all')}</Typography>
            </Box>
            {d.recentOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No recent orders found.')}</Typography>
            ) : (
              d.recentOrders.map((order, index) => (
                <Box
                  key={order.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/client-orders')}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate('/client-orders');
                    }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25,
                    borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider',
                    cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ width: 32, height: 32, borderRadius: `${radius.md}px`, bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', flexShrink: 0 }} fontSize={11} fontWeight={600}>
                    {order.customer ? order.customer.trim().charAt(0).toUpperCase() : '?'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{order.id} · {order.customer || p.t('Unknown user', { defaultValue: 'Unknown user' })}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.placedAt ? new Date(order.placedAt).toLocaleDateString() : ''} · {p.t('{{count}} items', { count: order.itemCount, defaultValue: `${order.itemCount} items` })}
                    </Typography>
                  </Box>
                  <StatusChip status={order.status} label={p.t(order.status)} />
                  <Typography variant="body2" fontWeight={700} sx={{ width: 80, textAlign: 'right' }}>{formatCurrency(order.total)}</Typography>
                  {order.status === 'pending' && (
                    <IconButton
                      size="small"
                      aria-label={p.t('Actions')}
                      onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuOrder(order); }}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
              <Typography variant="subtitle1">{p.t('Low stock', { defaultValue: 'Low stock' })}</Typography>
              <Typography component={Link} to="/inventory" variant="body2" fontWeight={600} sx={{ color: 'primary.main', textDecoration: 'none' }}>{p.t('Inventory')}</Typography>
            </Box>
            {d.lowStock.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('All inventory items have sufficient stock.', { defaultValue: 'All inventory items have sufficient stock.' })}</Typography>
            ) : (
              d.lowStock.map((item, index) => (
                <Box key={item.productId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{item.name}</Typography>
                  <Box sx={{ width: 56, height: 5, borderRadius: `${radius.pill}px`, bgcolor: 'action.selected', overflow: 'hidden' }}>
                    <Box sx={{ width: `${item.stockPct}%`, height: '100%', bgcolor: 'warning.main' }} />
                  </Box>
                  <Chip label={p.t('{{count}} left', { count: item.quantity, defaultValue: `${item.quantity} left` })} size="small" sx={{ bgcolor: 'warning.light', color: 'warning.dark' }} />
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeOrderMenu}>
        <MenuItem onClick={() => { p.openAcceptDialog(menuOrder); closeOrderMenu(); }}>
          <ListItemIcon><CheckCircleIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>{p.t('Accept', { defaultValue: 'Accept' })}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { p.openRejectDialog(menuOrder); closeOrderMenu(); }}>
          <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{p.t('Reject', { defaultValue: 'Reject' })}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Accept — requires an ETA, same flow as Client Orders */}
      <ConfirmDialog
        open={p.acceptOpen}
        onClose={() => p.setAcceptOpen(false)}
        tone="primary"
        title={p.t('Accept order #{{orderNumber}}', { orderNumber: p.acceptTarget?.id, defaultValue: `Accept order #${p.acceptTarget?.id}` })}
        description={p.t('An ETA is required before accepting.', { defaultValue: 'An ETA is required before accepting.' })}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Accept & set ETA', { defaultValue: 'Accept & set ETA' })}
        onConfirm={p.handleAccept}
        loading={p.accepting}
        confirmDisabled={!p.acceptEta}
      >
        <TextField
          fullWidth
          type="datetime-local"
          label={p.t('Set ETA')}
          InputLabelProps={{ shrink: true }}
          value={p.acceptEta}
          onChange={(e) => p.setAcceptEta(e.target.value)}
          sx={{ mt: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={() => p.applyQuickEta('tomorrowAm')} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('Tomorrow AM', { defaultValue: 'Tomorrow AM' })}
          </Button>
          <Button size="small" variant="outlined" onClick={() => p.applyQuickEta('in2days')} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('In 2 days', { defaultValue: 'In 2 days' })}
          </Button>
          <Button size="small" variant="outlined" onClick={() => p.applyQuickEta('nextMonday')} sx={{ borderRadius: `${radius.pill}px` }}>
            {p.t('Next Monday', { defaultValue: 'Next Monday' })}
          </Button>
        </Box>
      </ConfirmDialog>

      {/* Reject — requires a reason, same flow as Client Orders */}
      <ConfirmDialog
        open={p.rejectOpen}
        onClose={() => p.setRejectOpen(false)}
        tone="danger"
        title={p.t('Reject order #{{orderNumber}}', { orderNumber: p.rejectTarget?.id, defaultValue: `Reject order #${p.rejectTarget?.id}` })}
        description={p.t('Give a reason. The customer sees this on their order.', { defaultValue: 'Give a reason. The customer sees this on their order.' })}
        cancelLabel={p.t('Back')}
        confirmLabel={p.t('Reject Order')}
        onConfirm={p.handleReject}
        loading={p.rejecting}
        confirmDisabled={p.rejectReason.trim() === ''}
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          autoFocus
          value={p.rejectReason}
          onChange={(e) => p.setRejectReason(e.target.value)}
          placeholder={p.t('Please explain why you are rejecting this order...')}
          error={p.rejectReason.trim() === ''}
          helperText={p.rejectReason.trim() === '' ? p.t('Rejection reason is required') : ''}
          sx={{ mt: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          {p.rejectPresets.map((preset) => (
            <Button key={preset} size="small" variant="outlined" onClick={() => p.setRejectReason(preset)} sx={{ borderRadius: `${radius.pill}px` }}>
              {preset}
            </Button>
          ))}
        </Box>
      </ConfirmDialog>

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
